import { zodResolver } from "@hookform/resolvers/zod";
import { type AcceptInvitationInput, acceptInvitationSchema } from "@shelflist/shared";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Field, fieldA11y } from "@/components/field";
import { FormError } from "@/components/form-error";
import { InvalidLink } from "@/components/invalid-link";
import { PublicLayout } from "@/components/public-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, call } from "@/lib/api";
import { applyFieldErrors, useErrorMessage } from "@/lib/errors";
import { invitationQuery } from "@/lib/queries";
import { useAfterSignIn } from "@/lib/session";

export const Route = createFileRoute("/invite/$token")({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(invitationQuery(params.token)),
  component: InvitePage,
});

const FIELDS = ["name", "email", "password"] as const;

function InvitePage() {
  const { t } = useTranslation(["auth", "common"]);
  const { token } = Route.useParams();
  const { data: invitation } = useSuspenseQuery(invitationQuery(token));

  if (!invitation) {
    return (
      <InvalidLink title={t("invite.invalidTitle")} description={t("invite.invalidDescription")} />
    );
  }
  return (
    <PublicLayout
      title={t("invite.title")}
      description={t("invite.description", { role: t(`common:roles.${invitation.role}`) })}
    >
      <InviteForm token={token} fixedEmail={invitation.email} />
    </PublicLayout>
  );
}

function InviteForm({ token, fixedEmail }: { token: string; fixedEmail: string | null }) {
  const { t } = useTranslation(["auth", "common"]);
  const errorMessage = useErrorMessage();
  const afterSignIn = useAfterSignIn();
  const form = useForm<AcceptInvitationInput>({
    resolver: zodResolver(acceptInvitationSchema),
    defaultValues: { name: "", email: fixedEmail ?? "", password: "" },
  });
  const accept = useMutation({
    mutationFn: (values: AcceptInvitationInput) =>
      call(api.invitations[":token"].accept.$post({ param: { token }, json: values })),
    meta: { handlesErrors: true },
    onSuccess: () => afterSignIn("/"),
    onError: (error) => {
      applyFieldErrors(error, form.setError, FIELDS);
    },
  });
  const { errors } = form.formState;
  const hasFieldErrors = Object.keys(errors).length > 0;

  return (
    <form
      noValidate
      className="grid gap-4"
      onSubmit={form.handleSubmit((values) => accept.mutate(values))}
    >
      <FormError message={accept.error && !hasFieldErrors ? errorMessage(accept.error) : null} />
      <Field id="name" label={t("common:fields.name")} error={errors.name?.message}>
        <Input
          {...fieldA11y("name", errors.name?.message)}
          autoComplete="name"
          autoFocus
          {...form.register("name")}
        />
      </Field>
      <Field
        id="email"
        label={t("common:fields.email")}
        error={errors.email?.message}
        hint={fixedEmail ? t("invite.emailFixed") : undefined}
      >
        <Input
          {...fieldA11y("email", errors.email?.message)}
          type="email"
          autoComplete="username"
          readOnly={fixedEmail !== null}
          aria-readonly={fixedEmail !== null}
          className={fixedEmail ? "bg-muted" : undefined}
          {...form.register("email")}
        />
      </Field>
      <Field
        id="password"
        label={t("common:fields.password")}
        error={errors.password?.message}
        hint={t("common:passwordHint")}
      >
        <Input
          {...fieldA11y("password", errors.password?.message)}
          type="password"
          autoComplete="new-password"
          {...form.register("password")}
        />
      </Field>
      <Button type="submit" disabled={accept.isPending}>
        {t("invite.submit")}
      </Button>
    </form>
  );
}
